// frontend/src/theme.ts
import { createTheme, MantineColorsTuple } from '@mantine/core';

/**
 * Ocean-inspired color palette derived from the reference design.
 *
 * Reference CSS variables mapped to Mantine shades (0-9):
 *   --primary:     hsl(185, 72%, 40%)  -> teal[6]
 *   --teal-glow:   hsl(175, 80%, 45%)  -> teal[5]
 *   --secondary:   hsl(210, 60%, 15%)  -> ocean[8]
 *   --ocean-deep:  hsl(210, 60%, 8%)   -> ocean[9]
 *   --ocean-mid:   hsl(210, 55%, 15%)  -> ocean[7]
 *   --ocean-light: hsl(195, 60%, 25%)  -> ocean[6]
 *   --coral:       hsl(12, 80%, 60%)   -> coral[6]
 */

const ocean: MantineColorsTuple = [
  '#e8f4f8',  // 0 - lightest
  '#c5e4ed',  // 1
  '#93cfe0',  // 2
  '#5db8d2',  // 3
  '#3aa3c4',  // 4
  '#2690b5',  // 5
  '#1a6e8a',  // 6 - ocean-light: hsl(195, 60%, 25%)
  '#14415e',  // 7 - ocean-mid: hsl(210, 55%, 15%)
  '#0f2d47',  // 8 - secondary: hsl(210, 60%, 15%)
  '#091a2b',  // 9 - ocean-deep: hsl(210, 60%, 8%)
];

const teal: MantineColorsTuple = [
  '#e6fcf8',  // 0
  '#b3f5ea',  // 1
  '#80eddb',  // 2
  '#4de5cc',  // 3
  '#26d9bc',  // 4
  '#1fbfa5',  // 5 - teal-glow: hsl(175, 80%, 45%)
  '#178a82',  // 6 - primary: hsl(185, 72%, 40%)
  '#116b66',  // 7
  '#0b4d4a',  // 8
  '#062f2e',  // 9
];

const coral: MantineColorsTuple = [
  '#fff1ed',  // 0
  '#ffddd4',  // 1
  '#ffc4b5',  // 2
  '#ffa48f',  // 3
  '#ff8a6f',  // 4
  '#f07050',  // 5
  '#d9533a',  // 6 - coral: hsl(12, 80%, 60%)
  '#b5402c',  // 7
  '#912f20',  // 8
  '#6d2016',  // 9
];

export const theme = createTheme({
  primaryColor: 'teal',
  colors: {
    ocean,
    teal,
    coral,
  },

  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
  headings: {
    fontFamily: 'Space Grotesk, Inter, system-ui, sans-serif',
  },

  defaultRadius: 'md',

  components: {
    AppShell: {
      styles: {
        header: { backgroundColor: 'var(--rdh-header-bg)' },
      },
    },
    Card: {
      defaultProps: { radius: 12 },
      styles: {
        root: {
          backgroundColor: 'var(--rdh-card-bg)',
          transition: 'border-color 200ms ease',
          '&:hover': {
            borderColor: 'var(--mantine-primary-color-light)',
          },
        },
      },
    },
    Badge: {
      defaultProps: { radius: 'xl' },
    },
    Modal: {
      defaultProps: {
        radius: 'lg',
        centered: true,
        overlayProps: { opacity: 0.6, blur: 3 },
        padding: 'lg',
      },
      styles: {
        header: {
          borderBottom: '1px solid var(--mantine-color-default-border)',
          paddingBottom: 'var(--mantine-spacing-sm)',
          marginBottom: 'var(--mantine-spacing-sm)',
        },
        title: {
          fontFamily: 'Space Grotesk, sans-serif',
          fontWeight: 600,
        },
      },
    },
    Checkbox: {
      defaultProps: { radius: 'xl' },
    },
  },
});
